import { JSX } from "react";
import { CartesianGrid, Dot, Label, ResponsiveContainer, Scatter, ScatterChart, Tooltip, TooltipProps, XAxis, YAxis } from "recharts";
import { ValueType } from "recharts/types/component/DefaultTooltipContent";
import { HospitalAndPcpCountData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { tickFormatter } from "@app/overviews/network-benchmarking-dashboard/tiles/utility";
import { DashboardTile } from "@app/shared/qes-standards";

interface HospitalAndPcpCountProps {
  colorMap: Map<string, string>;
  customTooltip: (
    props: TooltipProps<ValueType, string | number>,
    titleKey: string,
  ) => JSX.Element;
  data: HospitalAndPcpCountData[]
  loading: boolean;
}

// this is an incomplete interface, but will suffice
interface HospitalAndPcpScatterProps {
  cx: number;
  cy: number;
  payload: {
    acuteHospitalCount: number;
    contractId: string;
    pcpCount: number;
    versionId: number;
  };
}

const HospitalAndPcpCount = (props: HospitalAndPcpCountProps): JSX.Element => {
  const renderDot = (cx: number, cy: number, payload: HospitalAndPcpCountData): JSX.Element => {
    // rendering individually in order to assign a radius size to the dot
    return (
      <Dot
        id={`benchmarking-dashboard__hospital-and-pcp-count--contract-${payload?.contractId}-hospital-${payload?.acuteHospitalCount}-pcp-${payload?.pcpCount}`}
        cx={cx}
        cy={cy}
        r={20}
        fill={props.colorMap.get(payload?.contractId)}
      />
    );
  }

  return (
    <DashboardTile
      loading={props.loading}
      title="Hospital and PCP Count"
      widthClass="col-lg-6"
    >
      {!!props.data?.length ? (
        <div style={{ height: "300px", width: "100%" }}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart
              margin={{
                bottom: 10,
                left: 0,
                right: 0,
                top: 20,
              }}
            >
              <CartesianGrid strokeDasharray="1 0" horizontal={false} />
              <XAxis dataKey="pcpCount" type="number" tickLine={false} tickFormatter={tickFormatter}>
                <Label value="PCP Count" offset={-10} position="insideBottom" />
              </XAxis>
              <YAxis dataKey="acuteHospitalCount" type="number" tickLine={false} tickFormatter={tickFormatter}>
                <Label value="Acute Hospital Count" position="insideLeft" angle={-90} offset={5} style={{ textAnchor: "middle" }} />
              </YAxis>
              <Tooltip content={(customTooltipProps) => props.customTooltip(customTooltipProps, "contractId")} />
              <Scatter data={props.data} shape={(props: HospitalAndPcpScatterProps) => renderDot(props.cx, props.cy, props.payload)} />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="copy-text-default font-italic mb-0">
          No results currently displayed. Input search parameters to get results.
        </p>
      )}
    </DashboardTile>
  )
}

export default HospitalAndPcpCount;
